// =============================
// File: src/components/user/DailyEarningsView.jsx
// =============================
import React, { useState, useEffect } from 'react';
import { DollarSign, Package, TrendingUp } from 'lucide-react';
import axios from 'axios';
import DateSelector from './DateSelector';

const API_URL = process.env.REACT_APP_API_URL || '';

function DailyEarningsView({ userId }) {
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [showCalendar, setShowCalendar] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEarnings();
  }, [selectedDate]);

  function getTodayDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async function fetchEarnings() {
    if (!selectedDate) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/users/${userId}/referral-earnings?date=${selectedDate}`
      );
      setEarnings(response.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      setEarnings(null);
    } finally {
      setLoading(false);
    }
  }

  function handleDateChange(newDate) {
    setSelectedDate(newDate);
  }

  function toggleCalendar() {
    setShowCalendar(!showCalendar);
  }

  function formatDate(isoDate) {
    if (!isoDate) return '';
    
    // Parse date string as local date (avoid timezone conversion)
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    return date.toLocaleDateString('es-PE', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  }

  return (
    <div className="space-y-6">
      {/* Date Selector */}
      <DateSelector
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        showCalendar={showCalendar}
        onToggleCalendar={toggleCalendar}
      />

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-montserrat">Cargando ganancias...</p>
        </div>
      )}

      {/* Earnings Display */}
      {!loading && earnings && (
        <>
          {/* Total Summary Card */}
          <div className="bg-gradient-to-br from-brand-primary to-[#8B2659] rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white/80 text-sm font-montserrat font-medium">
                  {formatDate(earnings.date)}
                </p>
                <h2 className="text-xl font-extrabold font-montserrat">
                  Ganancias del Día
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-white/60" />
                  <p className="text-white/80 text-xs font-montserrat font-medium">Total Ganado</p>
                </div>
                <p className="text-3xl font-extrabold font-montserrat">
                  S/ {earnings.total_earnings?.toFixed(2) || '0.00'}
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-5 h-5 text-white/60" />
                  <p className="text-white/80 text-xs font-montserrat font-medium">Total Envíos</p>
                </div>
                <p className="text-3xl font-extrabold font-montserrat">
                  {earnings.total_deliveries || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Referrals Breakdown */}
          {earnings.referrals && earnings.referrals.length > 0 ? (
            <div>
              <h3 className="text-lg font-extrabold font-montserrat text-slate-800 mb-4">
                Desglose por Referido ({earnings.referrals.length})
              </h3>

              <div className="space-y-3">
                {earnings.referrals.map((referral, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-brand-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-extrabold font-montserrat text-slate-800">
                            {referral.referred_username}
                          </h4>
                          <p className="text-xs text-slate-500 font-montserrat">
                            {referral.referred_phone}
                          </p>
                        </div>
                      </div>

                      <div className="text-right ml-4">
                        <p className="text-2xl font-extrabold font-montserrat text-brand-primary">
                          S/ {referral.earnings?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-xs text-slate-500 font-montserrat">
                          {referral.deliveries} {referral.deliveries === 1 ? 'envío' : 'envíos'}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs font-montserrat">
                        <span className="text-slate-500">
                          S/ 1.00 por envío × {referral.deliveries}
                        </span>
                        <span className="text-brand-green font-semibold">
                          = S/ {referral.earnings?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-extrabold font-montserrat text-slate-800 mb-2">
                Sin ganancias este día
              </h3>
              <p className="text-sm text-slate-500 font-montserrat">
                Tus referidos no realizaron envíos en esta fecha
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DailyEarningsView;